import { BrowserModule } from "@angular/platform-browser";
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptors, withInterceptorsFromDi } from "@angular/common/http";
import { NgModule } from "@angular/core";
import { AppRoutingModule } from "./app.routes";
import { AppComponent } from "./app.component";
import { BASE_API_URL, baseHttpRequestInterceptor } from "../../../shared/_interceptors/base-http-request.interceptor";
import { PendingRequestInterceptor } from "../../../shared/_interceptors/pending-http-request.interceptor";
import { ACCEPTS_TOKEN_IN_ADDRESS } from "../../../shared/_guards/auth.guard";
import { SIGN_IN_REDIRECT } from "../../../shared/_guards/sign-in-redirect";
import { createDashboardSignInRedirect } from "./_services/sign-in-redirect";
import { FontAwesomeModule } from "@fortawesome/angular-fontawesome";
import { CommonModule, DOCUMENT } from "@angular/common";
import { Router } from "@angular/router";
import { ReactiveFormsModule } from "@angular/forms";
import { BrowserAnimationsModule } from "@angular/platform-browser/animations";
import { MatPaginatorModule } from "@angular/material/paginator";
import { LoaderComponent } from "../../../shared/_components/loader/loader.component";
import { FormsModule } from "@angular/forms";
import { MatInputModule } from "@angular/material/input";
import { MAT_FORM_FIELD_DEFAULT_OPTIONS, MatFormFieldModule } from "@angular/material/form-field";
import { ToastrModule } from "ngx-toastr";
import { NotificationsService } from "../../../shared/_services/notifications.service";
import { UsersService } from "./users/users.service";
import { UsersComponent } from "./users/users.component";

import { environment } from "../environments/environment";
import { NavbarComponent } from "./_components/navbar/navbar.component";
import { LeftMenuComponent, APP_URL } from "./_components/left-menu/left-menu.component";
import { TradingSessionsComponent } from "./trading-sessions/trading-sessions.component";
import { TradingSessionsService } from "./trading-sessions/trading-sessions.service";
import { TradingSymbolsService } from "./trading-symbols/trading-symbols.service";
import { TradingSymbolsComponent } from "./trading-symbols/trading-symbols.component";
import { EditTradingSymbolComponent } from "./trading-symbols/edit/edit-trading-symbol.component";
import { TradingSymbolsListsComponent } from "./trading-symbols-lists/trading-symbols-lists.component";
import { EditTradingSymbolsListComponent } from "./trading-symbols-lists/edit/edit-trading-symbols-list.component";
import { ErrorHandlerService } from "../../../shared/_services/error-handler.service";
import { MatDialogModule } from "@angular/material/dialog";
import { SubscriptionEditDialogComponent } from "./users/subscription-edit-dialog/subscription-edit-dialog.component";
import { SubscriptionStatusPipe } from "../../../shared/_pipes/subscription-status.pipe";
import { ProviderTypePipe } from "../../../shared/_pipes/provider-type.pipe";
import { AccountTypePipe } from "../../../shared/_pipes/account-type.pipe";
import { BotStatusTypePipe } from "../../../shared/_pipes/bot-status-type.pipe";

import { MatIconModule } from "@angular/material/icon";
import { MatButtonModule } from "@angular/material/button";
import { MatSelectModule } from "@angular/material/select";
import { MatCheckboxModule } from "@angular/material/checkbox";
import { MatTableModule } from "@angular/material/table";
import { MatDatepickerModule } from "@angular/material/datepicker";
import { MatNativeDateModule } from "@angular/material/core";
import { MatMenuModule } from "@angular/material/menu";
import { MatChipsModule } from "@angular/material/chips";
import { PricingPackagesService } from "./_services/pricing-packages.service";
import { PricingPackagesComponent } from "./pricing-packages/pricing-packages.component";
import { PricingPackageEditDialogComponent } from "./pricing-packages/pricing-package-edit-dialog/pricing-package-edit-dialog.component";
import { PricingPackageTypePipe } from "./_pipes/pricing-package-type.pipe";
import { MarketplaceComponent } from "./marketplace/marketplace.component";
import { EditMarketplaceComponent } from "./marketplace/edit/edit-marketplace.component";
import { MarketplaceService } from "./marketplace/marketplace.service";
import { TradingSymbolsListsService } from "./_services/trading-symbols-lists.service";
import { StatusComponent } from "./status/status.component";
import { StatusService } from "./status/status.service";
import { MatSliderModule } from "@angular/material/slider";
import { MatSlideToggleModule } from "@angular/material/slide-toggle";
import { MatButtonToggleModule } from "@angular/material/button-toggle";
import { MatDividerModule } from "@angular/material/divider";
import { MatGridListModule } from "@angular/material/grid-list";
import { MatCardModule } from "@angular/material/card";
import { MatTooltipModule } from "@angular/material/tooltip";
import { UserInfoDialogComponent } from "./users/user-info-dialog/user-info-dialog.component";
import { TradingLockComponent } from "./users/trading-lock/trading-lock.component";
import { TimerComponent } from "../../../shared/_components/timer/timer.component";
import { SharedLeftMenuComponent } from "../../../shared/_components/left-menu/left-menu.component";
import { SharedNavbarComponent } from "../../../shared/_components/navbar/navbar.component";
import { MartingaleStrategyOptionsEditComponent } from "../../../shared/_components/strategy-options-edit/martingale-strategy-options-edit/martingale-strategy-options-edit.component";
import { CustomStrategyOptionsEditComponent } from "../../../shared/_components/strategy-options-edit/custom-strategy-options-edit/custom-strategy-options-edit.component";
import { provideTranslateService } from "@ngx-translate/core";
import { provideTranslateHttpLoader } from "@ngx-translate/http-loader";

@NgModule({
    declarations: [
        AppComponent,
        StatusComponent,
        UsersComponent,
        NavbarComponent,
        LeftMenuComponent,
        TradingSessionsComponent,
        TradingSymbolsComponent,
        EditTradingSymbolComponent,
        TradingSymbolsListsComponent,
        EditTradingSymbolsListComponent,
        SubscriptionEditDialogComponent,
        PricingPackagesComponent,
        PricingPackageEditDialogComponent,
        MarketplaceComponent,
        EditMarketplaceComponent,
        UserInfoDialogComponent,
    ],
    imports: [
        CommonModule,
        BrowserModule,
        AppRoutingModule,
        FontAwesomeModule,
        ReactiveFormsModule,
        BrowserAnimationsModule,
        FormsModule,
        MatPaginatorModule,
        MatInputModule,
        MatIconModule,
        MatButtonModule,
        MatSelectModule,
        MatCheckboxModule,
        MatFormFieldModule,
        MatTableModule,
        MatDialogModule,
        MatDatepickerModule,
        MatNativeDateModule,
        MatMenuModule,
        MatSliderModule,
        MatSlideToggleModule,
        MatButtonToggleModule,
        MatDividerModule,
        SharedLeftMenuComponent,
        SharedNavbarComponent,
        MatGridListModule,
        MatCardModule,
        MatTooltipModule,
        MatChipsModule,
        LoaderComponent,
        MartingaleStrategyOptionsEditComponent,
        CustomStrategyOptionsEditComponent,
        ToastrModule.forRoot({
            timeOut: 3000,
            positionClass: "toast-bottom-right",
            preventDuplicates: true,
            closeButton: true,
            tapToDismiss: true,
            maxOpened: 3,
        }), // ToastrModule added
        SubscriptionStatusPipe,
        PricingPackageTypePipe,
        ProviderTypePipe,
        AccountTypePipe,
        BotStatusTypePipe,
        TimerComponent,
        TradingLockComponent,
    ],
    providers: [
        provideTranslateService({
            loader: provideTranslateHttpLoader({
                prefix: "/assets/i18n/",
                suffix: ".json",
            }),
            fallbackLang: "en",
        }),
        UsersService,
        TradingSessionsService,
        TradingSymbolsService,
        TradingSymbolsListsService,
        PricingPackagesService,
        MarketplaceService,
        StatusService,
        MatDatepickerModule,
        MatNativeDateModule,
        provideHttpClient(withInterceptors([baseHttpRequestInterceptor()]), withInterceptorsFromDi()),
        PendingRequestInterceptor,
        ErrorHandlerService,
        NotificationsService,
        {
            provide: HTTP_INTERCEPTORS,
            useExisting: PendingRequestInterceptor,
            multi: true,
        },
        { provide: BASE_API_URL, useValue: environment.apiUrl },
        { provide: APP_URL, useValue: environment.appUrl },
        { provide: ACCEPTS_TOKEN_IN_ADDRESS, useValue: true },
        { provide: SIGN_IN_REDIRECT, useFactory: createDashboardSignInRedirect, deps: [Router, DOCUMENT] },
        { provide: MAT_FORM_FIELD_DEFAULT_OPTIONS, useValue: { appearance: "outline" } },
    ],
    bootstrap: [AppComponent],
})
export class AppModule {}
